"use client"

import { useEffect, useRef } from 'react'
import Script from 'next/script'
import { usePathname } from 'next/navigation'
import { getContactMethod, trackContact, trackLead } from '@/lib/meta-pixel'

declare global {
  interface Window {
    fbq?: (...args: any[]) => void
  }
}

export const META_PIXEL_ID = process.env.NEXT_PUBLIC_META_PIXEL_ID || '813693084114874'

/**
 * Meta Pixel (Facebook) para todo el sitio.
 * - Carga el script oficial de Meta con next/script (afterInteractive).
 * - Respeta el CookieBanner: si el usuario rechazó cookies de marketing,
 *   se revoca el consentimiento y el pixel no envía eventos.
 * - Envía PageView en cada cambio de ruta (App Router no recarga la página).
 */
export default function MetaPixel() {
  const pathname = usePathname()
  const isFirstRender = useRef(true)

  // PageView en navegación interna (la primera vista la envía el script base)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    window.fbq?.('track', 'PageView')
  }, [pathname])

  // Sincroniza con cambios de preferencias de cookies
  useEffect(() => {
    const onConsentChange = (e: Event) => {
      const prefs = (e as CustomEvent<{ marketing?: boolean }>).detail
      if (!window.fbq || !prefs) return
      window.fbq('consent', prefs.marketing ? 'grant' : 'revoke')
      if (prefs.marketing) window.fbq('track', 'PageView')
    }
    window.addEventListener('cookie-consent-change', onConsentChange)
    return () => window.removeEventListener('cookie-consent-change', onConsentChange)
  }, [])

  // Contact: clics en cualquier enlace de WhatsApp, teléfono o correo del sitio
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null
      const anchor = target?.closest?.('a[href]') as HTMLAnchorElement | null
      if (!anchor) return
      const method = getContactMethod(anchor.getAttribute('href') || '')
      if (method) trackContact(method)
    }
    // capture: se registra aunque el componente detenga la propagación
    document.addEventListener('click', onClick, true)
    return () => document.removeEventListener('click', onClick, true)
  }, [])

  // Lead: envío completado de formularios JotForm embebidos (iframe)
  useEffect(() => {
    const sent = new Set<string>()
    const onMessage = (e: MessageEvent) => {
      let host = ''
      try { host = new URL(e.origin).hostname } catch { return }
      if (!/(^|\.)jotform\.(com|me|us)$/i.test(host)) return
      let data: any = e.data
      if (typeof data === 'string') {
        try { data = JSON.parse(data) } catch { /* formato no JSON */ }
      }
      const isSubmit =
        (data && typeof data === 'object' && data.action === 'submission-completed') ||
        (typeof e.data === 'string' && e.data.includes('submission-completed'))
      if (!isSubmit) return
      const formId = String((data && data.formID) || 'jotform')
      // evita duplicados si JotForm emite el mensaje más de una vez
      const key = `${formId}-${Math.floor(Date.now() / 5000)}`
      if (sent.has(key)) return
      sent.add(key)
      trackLead({ content_name: `jotform_${formId}` })
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  return (
    <>
      <Script id="meta-pixel" strategy="afterInteractive">
        {`
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          try {
            var c = JSON.parse(localStorage.getItem('cookie-consent') || 'null');
            if (c && c.marketing === false) { fbq('consent', 'revoke'); }
          } catch (e) {}
          fbq('init', '${META_PIXEL_ID}');
          fbq('track', 'PageView');
        `}
      </Script>
      <noscript>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          height="1"
          width="1"
          style={{ display: 'none' }}
          src={`https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1`}
          alt=""
        />
      </noscript>
    </>
  )
}
